import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as HiIcons from 'react-icons/hi';
import SafeIcon from '../../common/SafeIcon';

const { FiTrendingUp, FiZap, FiHeart, FiHeadphones } = FiIcons;
const { HiSparkles } = HiIcons;

const DesiredOutcome = () => {
  const outcomes = [
    {
      icon: FiHeart,
      title: "Sound like you",
      description: "No more generic-AI blend. Every chapter reads in your own voice, so readers stay with your story.",
      color: "from-primary-500 to-primary-600",
      bgColor: "from-primary-50 to-parchment-100"
    },
    {
      icon: FiZap,
      title: "Finish faster",
      description: "Outline to full draft with AI that writes in your style, so you finish the book instead of fighting the blank page.",
      color: "from-primary-400 to-primary-600",
      bgColor: "from-primary-50 to-secondary-50"
    },
    {
      icon: FiTrendingUp,
      title: "Publish without rejections",
      description: "Live KDP Preflight keeps your manuscript compliant as you write, so you hit publish on the first try.",
      color: "from-secondary-500 to-secondary-600",
      bgColor: "from-secondary-50 to-parchment-100"
    },
    {
      icon: FiHeadphones,
      title: "Ship a sellable audiobook",
      description: "Turn your manuscript into a polished audiobook in hours, not weeks - AI narration mastered to ACX spec, ready for Amazon Virtual Voice (via KDP) and sanctioned wide distribution. No per-book narrator fees, no royalty splits.",
      color: "from-primary-500 to-secondary-500",
      bgColor: "from-primary-50 to-secondary-50"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-white to-parchment-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Imagine Writing That's{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">
              In Your Voice, Faster, KDP-Ready — and Audiobook-Ready
            </span>
          </motion.h2>
          <motion.p
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            The writing studio that learns your voice — write in your own style, stay KDP-ready as you go, and export clean.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {outcomes.map((outcome, index) => (
            <motion.div
              key={index}
              className={`relative overflow-hidden rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br ${outcome.bgColor}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              whileHover={{ y: -5 }}
            >
              <div className="relative z-10">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${outcome.color} mb-6`}>
                  <SafeIcon icon={outcome.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {outcome.title}
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {outcome.description}
                </p>
              </div>
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                <SafeIcon icon={HiSparkles} className="w-full h-full" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DesiredOutcome;